import { useState, useCallback, useContext } from "react"
import { DateTime } from "luxon"
import { useCookies } from "react-cookie"
import { AdminContext } from "~/contexts/AdminContext"
import {
  reservationReportFilterCookie,
  addonReportFilterCookie,
  paymentsReportFilterCookie,
} from "~/constants/cookies"

interface FiltersState {
  selectedStartDate: DateTime
  selectedEndDate: DateTime
  selectedFilterByForReservation: string
  selectedFilterByForPayments: string
  selectedFilterByForAddon: string
  showOnlyStripePayments: boolean
  hideDeletedReservationPayments: boolean
  page: number
  perPage: number
  isDrawerOpen: boolean
  tempFilters: {
    selectedStartDate: DateTime
    selectedEndDate: DateTime
    selectedFilterByForReservation: string
    selectedFilterByForPayments: string
    selectedFilterByForAddon: string
    showOnlyStripePayments: boolean
    hideDeletedReservationPayments: boolean
  }
}

export const useReportsFilters = () => {
  const { camp } = useContext(AdminContext)
  const timezone = camp.timezone

  const [cookies] = useCookies([
    reservationReportFilterCookie,
    addonReportFilterCookie,
    paymentsReportFilterCookie,
  ])

  const initialState: FiltersState = {
    selectedStartDate: DateTime.now().minus({ days: 7 }).startOf('day'),
    selectedEndDate: DateTime.now().endOf('day'),
    selectedFilterByForReservation: cookies[reservationReportFilterCookie] || "start_date",
    selectedFilterByForPayments: cookies[paymentsReportFilterCookie] || "revenue_booked",
    selectedFilterByForAddon: cookies[addonReportFilterCookie] || "purchase_date",
    showOnlyStripePayments: false,
    hideDeletedReservationPayments: true,
    page: 1,
    perPage: 50,
    isDrawerOpen: false,
    tempFilters: {
      selectedStartDate: DateTime.now().minus({ days: 7 }).startOf('day'),
      selectedEndDate: DateTime.now().endOf('day'),
      selectedFilterByForReservation: cookies[reservationReportFilterCookie] || "start_date",
      selectedFilterByForPayments: cookies[paymentsReportFilterCookie] || "revenue_booked",
      selectedFilterByForAddon: cookies[addonReportFilterCookie] || "purchase_date",
      showOnlyStripePayments: false,
      hideDeletedReservationPayments: true,
    }
  }

  const [filters, setFilters] = useState<FiltersState>(initialState)

  const setSelectedStartDate = useCallback((date: DateTime) => {
    const dateWithOffset = date.setZone(timezone, { keepLocalTime: true })
    setFilters(prev => ({
      ...prev,
      selectedStartDate: dateWithOffset.startOf('day')
    }))
  }, [timezone])

  const setSelectedEndDate = useCallback((date: DateTime) => {
    if (!date) return
    const dateWithOffset = date.setZone(timezone, { keepLocalTime: true })
    setFilters(prev => ({
      ...prev,
      selectedEndDate: dateWithOffset.endOf('day')
    }))
  }, [timezone])

  const setTempStartDate = useCallback((date: DateTime) => {
    const dateWithOffset = date.setZone(timezone, { keepLocalTime: true })
    setFilters(prev => ({
      ...prev,
      tempFilters: {
        ...prev.tempFilters,
        selectedStartDate: dateWithOffset.startOf('day')
      }
    }))
  }, [timezone])

  const setTempEndDate = useCallback((date: DateTime) => {
    if (!date) return
    const dateWithOffset = date.setZone(timezone, { keepLocalTime: true })
    setFilters(prev => ({
      ...prev,
      tempFilters: {
        ...prev.tempFilters,
        selectedEndDate: dateWithOffset.endOf('day')
      }
    }))
  }, [timezone])

  const setPage = useCallback((newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }, [])

  const toggleDrawer = useCallback((isOpen: boolean) => {
    setFilters(prev => ({ ...prev, isDrawerOpen: isOpen }))
  }, [])

  const updateTempFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      tempFilters: {
        selectedStartDate: prev.selectedStartDate,
        selectedEndDate: prev.selectedEndDate,
        selectedFilterByForReservation: prev.selectedFilterByForReservation,
        selectedFilterByForPayments: prev.selectedFilterByForPayments,
        selectedFilterByForAddon: prev.selectedFilterByForAddon,
        showOnlyStripePayments: prev.showOnlyStripePayments,
        hideDeletedReservationPayments: prev.hideDeletedReservationPayments,
      }
    }))
  }, [])

  const applyTempFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      ...prev.tempFilters,
      isDrawerOpen: false,
      page: 1
    }))
  }, [])

  const updateFilters = useCallback((key: keyof FiltersState, value: any, isTemp: boolean = false) => {
    setFilters(prev => {
      if (isTemp) {
        return {
          ...prev,
          tempFilters: {
            ...prev.tempFilters,
            [key]: value
          }
        }
      }
      return {
        ...prev,
        [key]: value,
        page: 1
      }
    })
  }, [])

  return {
    filters,
    setSelectedStartDate,
    setSelectedEndDate,
    setTempStartDate,
    setTempEndDate,
    setPage,
    toggleDrawer,
    updateTempFilters,
    applyTempFilters,
    updateFilters: (key: keyof FiltersState, isTemp: boolean = false) => (value: any) =>
      updateFilters(key, value, isTemp)
  }
}
